// AlertTape — sticky-bottom 24px Bloomberg-style ticker.
//
// Receives live signals via the `events` prop (the parent wires it to
// useIntelFeed's `onLiveEvent`). New chips slide in from the right; severity
// >= 80 chips persist until dismissed; lower severity chips fade after 8s.
//
// M4 (2026-05-18): dedup + cooldown + persistent dismiss.
//   * computeAlertKey() turns each signal into a stable 16-hex key.
//   * shouldDisplay() drops re-fires inside the cooldown window
//     (5min for CRITICAL ≥80, 30min otherwise).
//   * Dismissed keys persist in IDB for 7 days (eidollon-dismissed-alerts).
//   * Chime is gated by a 60s global cooldown and by the
//     `alert-chime-classified` localStorage opt-in for CUI/RESTRICTED.
//
// Accessibility: `aria-live="polite"` for normal/elevated, `assertive` for
// HOT only. `prefers-reduced-motion` disables slide-in (chips appear instantly).

import {
  type RecentlySeenMap,
  computeAlertKey,
  isClassified,
  shouldDisplay,
} from '@/screens/intel/alert-dedup';
import { SEVERITY_COLOR, severityBucket } from '@/screens/intel/columns';
import { gdeltCodes, type intel as i } from '@eidollon/contracts';
import { type DismissedStore, openDismissedStore } from '@eidollon/intel-feed-client';
import { useEffect, useMemo, useRef, useState } from 'react';

const FADE_AFTER_MS = 8_000;
const HOT_SEVERITY = 80;
const QUEUE_HARD_CAP = 64;
const CHIME_GLOBAL_COOLDOWN_MS = 60_000;
/** localStorage flag controlling chimes on CUI/RESTRICTED alerts.
 *  TODO(M4 follow-up): expose a settings UI for this — today it's an
 *  install-time / DevTools toggle. */
const CHIME_CLASSIFIED_OPTIN_KEY = 'alert-chime-classified';

interface ChipState {
  signal: i.IntelSignal;
  receivedAt: number;
  pinned: boolean;
  alertKey: string;
}

export interface AlertTapeProps {
  /** Live signals as they arrive — append-only; the tape derives chips and
   *  fades them out internally. Order: oldest first. */
  events?: readonly i.IntelSignal[];
  /** When true, queue overflow shows a `+N NEW` chip instead of rendering. */
  paused?: boolean;
  onResume?: () => void;
  /** Tenant scope for the dismissed-keys IDB store. When null, persistence
   *  is disabled (chips still dedup in-memory). */
  tenantId?: string | null;
  /** Test hook — pass fake-indexeddb's factory; falls back to global IDB. */
  idbFactory?: IDBFactory;
  /** Test hook — called when a chime would fire. The component does not
   *  ship audio bytes yet; this is the integration point for a future
   *  audio asset and for vitest assertions. */
  onChime?: (signal: i.IntelSignal) => void;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function chimeOptInForClassified(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(CHIME_CLASSIFIED_OPTIN_KEY) === 'true';
  } catch {
    return false;
  }
}

export function AlertTape({
  events = [],
  paused = false,
  onResume,
  tenantId,
  idbFactory,
  onChime,
}: AlertTapeProps) {
  const [chips, setChips] = useState<ChipState[]>([]);
  const [queued, setQueued] = useState(0);
  const seenIdsRef = useRef<Set<string>>(new Set());
  const recentlySeenRef = useRef<RecentlySeenMap>(new Map());
  const dismissedKeysRef = useRef<Set<string>>(new Set());
  const lastChimeAtRef = useRef<number>(0);
  const storeRef = useRef<DismissedStore | null>(null);
  const reduce = useMemo(prefersReducedMotion, []);

  // Hydrate dismissed keys + open the IDB store. Tenant-scoped; null tenant
  // is a no-op (in-memory dedup still works without persistence).
  useEffect(() => {
    if (!tenantId) return;
    let alive = true;
    (async () => {
      try {
        const store = await openDismissedStore({ tenantId, factory: idbFactory });
        if (!alive) {
          store.close();
          return;
        }
        storeRef.current = store;
        const entries = await store.list();
        if (!alive) return;
        for (const e of entries) dismissedKeysRef.current.add(e.key);
      } catch {
        // IDB unavailable (private mode, SSR, etc.) — dedup degrades to
        // in-memory only. Not a failure mode worth surfacing in UI.
      }
    })();
    return () => {
      alive = false;
      storeRef.current?.close();
      storeRef.current = null;
    };
  }, [tenantId, idbFactory]);

  // Append new events as chips; pinned if HOT. Applies dedup/dismiss gates
  // and the chime cooldown. Async because computeAlertKey() awaits
  // crypto.subtle.digest().
  useEffect(() => {
    if (events.length === 0) return;
    let cancelled = false;

    (async () => {
      const fresh: ChipState[] = [];
      for (const s of events) {
        if (seenIdsRef.current.has(s.id)) continue;
        seenIdsRef.current.add(s.id);

        const key = await computeAlertKey({
          id: s.id,
          source: s.source,
          summary: s.summary,
        });
        if (dismissedKeysRef.current.has(key)) continue;

        const now = Date.now();
        const decision = shouldDisplay(s, key, recentlySeenRef.current, now);
        if (!decision.display) continue;

        // Chime gate: 60s global cooldown, plus CUI/RESTRICTED opt-in.
        const classified = isClassified(s.classification);
        const optIn = chimeOptInForClassified();
        const chimeAllowed =
          (!classified || optIn) && now - lastChimeAtRef.current >= CHIME_GLOBAL_COOLDOWN_MS;
        if (chimeAllowed && onChime) {
          lastChimeAtRef.current = now;
          onChime(s);
        }

        fresh.push({
          signal: s,
          receivedAt: now,
          pinned: s.severity >= HOT_SEVERITY,
          alertKey: key,
        });
      }
      if (cancelled || fresh.length === 0) return;

      if (paused) {
        setQueued((n) => n + fresh.length);
        return;
      }
      setChips((prev) => {
        const next = [...fresh, ...prev];
        // Keep tape bounded; oldest non-pinned chips drop off first.
        if (next.length <= QUEUE_HARD_CAP) return next;
        const pinned = next.filter((c) => c.pinned);
        const others = next.filter((c) => !c.pinned).slice(0, QUEUE_HARD_CAP - pinned.length);
        return [...pinned, ...others];
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [events, paused, onChime]);

  // Fade-out timer for non-pinned chips.
  useEffect(() => {
    if (chips.length === 0) return;
    const t = window.setInterval(() => {
      const cutoff = Date.now() - FADE_AFTER_MS;
      setChips((prev) => prev.filter((c) => c.pinned || c.receivedAt > cutoff));
    }, 1_000);
    return () => window.clearInterval(t);
  }, [chips.length]);

  const dismissChip = (chip: ChipState) => {
    dismissedKeysRef.current.add(chip.alertKey);
    setChips((prev) => prev.filter((c) => c.alertKey !== chip.alertKey));
    // Fire-and-forget; IDB write failure degrades dismissal to in-session.
    // Iron Rule 2 note: dismissals are user-UI noise control, NOT a
    // security-relevant decision — intentionally not audited.
    void storeRef.current?.add(chip.alertKey).catch(() => {});
  };

  const hottest = chips[0]?.signal.severity ?? 0;
  const live = hottest >= HOT_SEVERITY ? 'assertive' : 'polite';

  return (
    <div
      data-component="AlertTape"
      aria-live={live}
      aria-label="live alert tape"
      style={{
        position: 'sticky',
        bottom: 0,
        left: 0,
        right: 0,
        height: 24,
        padding: '0 8px',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        background: 'var(--bg-pane)',
        borderTop: '1px solid var(--bd)',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        fontFamily: 'var(--mono)',
        fontSize: 11,
        color: 'var(--fg-2)',
        zIndex: 40,
      }}
    >
      {paused && queued > 0 && onResume && (
        <button
          type="button"
          onClick={() => {
            onResume();
            setQueued(0);
          }}
          style={{
            height: 18,
            padding: '0 6px',
            background: 'var(--amber-soft)',
            border: '1px solid var(--amber-line)',
            color: 'var(--amber)',
            fontFamily: 'var(--mono)',
            fontSize: 10,
            cursor: 'pointer',
          }}
        >
          +{queued} NEW · resume ▶
        </button>
      )}

      <div style={{ flex: 1, display: 'flex', gap: 8, overflow: 'hidden' }}>
        {chips.map((c) => (
          <Chip key={c.signal.id} chip={c} reduceMotion={reduce} onDismiss={dismissChip} />
        ))}
        {chips.length === 0 && (
          <span style={{ color: 'var(--fg-3)' }}>● live · awaiting signals</span>
        )}
      </div>

      {onResume && (
        <button
          type="button"
          onClick={onResume}
          aria-label={paused ? 'resume' : 'pause'}
          title={paused ? 'resume live tape' : 'pause live tape'}
          style={{
            height: 18,
            padding: '0 6px',
            background: 'transparent',
            border: '1px solid var(--bd)',
            color: 'var(--fg-3)',
            fontFamily: 'var(--mono)',
            fontSize: 10,
            cursor: 'pointer',
          }}
        >
          {paused ? '▶' : '⏸'}
        </button>
      )}
    </div>
  );
}

function Chip({
  chip,
  reduceMotion,
  onDismiss,
}: {
  chip: ChipState;
  reduceMotion: boolean;
  onDismiss: (chip: ChipState) => void;
}) {
  const code = severityBucket(chip.signal.severity);
  const tone = SEVERITY_COLOR[code];
  const ts = chip.signal.occurred_at.slice(11, 16);
  const transition = reduceMotion ? undefined : 'transform 90ms linear, opacity 240ms linear';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 18,
        padding: '0 6px',
        background: 'var(--bg-pane-2)',
        borderLeft: `2px solid ${tone}`,
        color: 'var(--fg-1)',
        transition,
      }}
    >
      <span style={{ color: tone, fontWeight: 600 }}>●{code}</span>
      <span style={{ color: 'var(--fg-3)' }}>{ts}</span>
      <span style={{ color: 'var(--cyan)' }}>{chip.signal.signal_type}</span>
      <span
        style={{
          color: 'var(--fg-1)',
          maxWidth: 320,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {gdeltCodes.signalTitle(chip.signal)}
      </span>
      <button
        type="button"
        onClick={() => onDismiss(chip)}
        aria-label="dismiss alert"
        title="dismiss (won't show again for 7d)"
        style={{
          marginLeft: 2,
          height: 14,
          width: 14,
          padding: 0,
          background: 'transparent',
          border: 'none',
          color: 'var(--fg-3)',
          fontFamily: 'var(--mono)',
          fontSize: 11,
          lineHeight: '14px',
          cursor: 'pointer',
        }}
      >
        ×
      </button>
    </span>
  );
}
